import {
  useCallback,
  useContext,
  useLayoutEffect,
  useEffect,
  useState,
  useMemo,
} from "react"
import { createPortal } from "react-dom"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Pencil,
  Plus,
  Search,
} from "lucide-react"
import { useAppSelector } from "@/reducers/hooks"
import { selectTabs, TAB_TYPES } from "@/reducers/tabsSlice"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands, { GpsConfigEntry } from "@/api/adHocCommands"
import XMPPError from "@xmpp/error"
import Loader from "@/components/Loader"
import AddGpsProviderModal from "./AddGpsProviderModal"
import EditGpsProviderModal from "./EditGpsProviderModal"
import { useUserPermissions } from "@/hooks/useUserPermissions"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"

type SortKey = "provider" | "status" | "plates" | "added"
type SortDir = "asc" | "desc"

function statusRank(cfg: GpsConfigEntry): number {
  if (cfg.unreachableSince) return 2
  if (!cfg.enabled) return 1
  return 0
}

function sortConfigs(
  configs: GpsConfigEntry[],
  key: SortKey,
  dir: SortDir,
): GpsConfigEntry[] {
  const sorted = [...configs].sort((a, b) => {
    switch (key) {
      case "provider":
        return a.provider.localeCompare(b.provider)
      case "status":
        return statusRank(a) - statusRank(b)
      case "plates":
        return a.plates.length - b.plates.length
      case "added":
        return (a.createdAt ?? "").localeCompare(b.createdAt ?? "")
    }
  })
  return dir === "asc" ? sorted : sorted.reverse()
}

function formatDate(iso: string): string {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString(getIntlLocale())
  } catch {
    return iso
  }
}

function SortableHead({
  label,
  sortKey,
  active,
  dir,
  onSort,
}: {
  label: string
  sortKey: SortKey
  active: SortKey
  dir: SortDir
  onSort: (key: SortKey) => void
}) {
  return (
    <TableHead
      className="cursor-pointer select-none"
      onClick={() => onSort(sortKey)}
    >
      <span className="flex items-center gap-1">
        {label}
        {active === sortKey ? (
          dir === "asc" ? (
            <ChevronUp className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 text-muted-foreground/40" />
        )}
      </span>
    </TableHead>
  )
}

export function GpsManagement() {
  const { t } = useTranslation()
  const { activeTab } = useAppSelector(selectTabs)
  const [isOpen, setIsOpen] = useState(false)
  const [configs, setConfigs] = useState<GpsConfigEntry[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState<GpsConfigEntry | null>(
    null,
  )
  const [fetchKey, setFetchKey] = useState(0)
  const [search, setSearch] = useState("")
  const [showDisabled, setShowDisabled] = useState(false)
  const [sortKey, setSortKey] = useState<SortKey>("plates")
  const [sortDir, setSortDir] = useState<SortDir>("desc")

  const { client } = useContext(XmppContext)
  const { hasPermission } = useUserPermissions()

  const handleProviderAdded = useCallback(() => {
    setFetchKey((k) => k + 1)
  }, [])

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
        return prev
      }
      setSortDir("asc")
      return key
    })
  }, [])

  const filteredAndSorted = useMemo(() => {
    const q = search.trim().toLowerCase()
    const visible = showDisabled
      ? configs
      : configs.filter((cfg) => cfg.enabled)
    const filtered = q
      ? visible.filter(
          (cfg) =>
            cfg.provider.toLowerCase().includes(q) ||
            cfg.name.toLowerCase().includes(q) ||
            cfg.plates.some((p) => p.toLowerCase().includes(q)),
        )
      : visible
    return sortConfigs(filtered, sortKey, sortDir)
  }, [configs, search, showDisabled, sortKey, sortDir])

  useLayoutEffect(() => {
    setIsOpen(activeTab === TAB_TYPES.GPS_MANAGEMENT)
  }, [activeTab])

  useEffect(() => {
    if (!isOpen) return

    setLoading(true)
    setLoadError(null)
    AdHocCommands.listGpsConfigs(client)
      .then((entries) => {
        setConfigs(entries)
      })
      .catch((err) => {
        console.warn("Failed to fetch GPS configs", err)
        // "" means no server text: the fallback is translated where shown.
        setLoadError(err instanceof XMPPError && err.text ? err.text : "")
      })
      .finally(() => {
        setLoading(false)
      })
  }, [isOpen, client, fetchKey])

  if (!isOpen) return null

  return (
    <>
      <AddGpsProviderModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleProviderAdded}
      />
      <EditGpsProviderModal
        config={editingConfig}
        onClose={() => setEditingConfig(null)}
        onSuccess={handleProviderAdded}
      />
      {createPortal(
        <div className="fixed inset-0 left-[49px] z-50 flex w-[100vw-48px] items-center justify-center rounded-md bg-background">
          <div className="relative h-full w-full">
            <div className="h-full w-full overflow-auto">
              <div className="p-6">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-semibold">
                      {t("fleet.gps.title")}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {t("fleet.gps.subtitle")}
                    </p>
                  </div>
                  {hasPermission("gps.manage_providers") && (
                    <Button
                      onClick={() => setIsAddModalOpen(true)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {t("fleet.gps.addProvider")}
                    </Button>
                  )}
                </div>

                <div className="mb-4 flex items-center gap-4">
                  <div className="relative w-72">
                    <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("fleet.gps.search")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show-disabled"
                      checked={showDisabled}
                      onCheckedChange={(v) => setShowDisabled(Boolean(v))}
                    />
                    <Label
                      htmlFor="show-disabled"
                      className="cursor-pointer text-sm font-normal"
                    >
                      {t("fleet.gps.showDisabled")}
                    </Label>
                  </div>
                </div>

                {loading ? (
                  <div className="flex h-40 items-center justify-center">
                    <Loader />
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableHead
                            label={t("fleet.gps.provider")}
                            sortKey="provider"
                            active={sortKey}
                            dir={sortDir}
                            onSort={handleSort}
                          />
                          <SortableHead
                            label={t("fleet.gps.status")}
                            sortKey="status"
                            active={sortKey}
                            dir={sortDir}
                            onSort={handleSort}
                          />
                          <SortableHead
                            label={t("fleet.gps.plates")}
                            sortKey="plates"
                            active={sortKey}
                            dir={sortDir}
                            onSort={handleSort}
                          />
                          <SortableHead
                            label={t("fleet.gps.added")}
                            sortKey="added"
                            active={sortKey}
                            dir={sortDir}
                            onSort={handleSort}
                          />
                          <TableHead className="w-[52px]" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadError !== null ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="h-24 text-center text-red-600 dark:text-red-400"
                            >
                              {loadError || t("fleet.gps.loadFailed")}
                            </TableCell>
                          </TableRow>
                        ) : filteredAndSorted.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="h-24 text-center text-muted-foreground"
                            >
                              {configs.length === 0
                                ? t("fleet.gps.empty")
                                : t("fleet.gps.noMatch")}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredAndSorted.map((cfg) => (
                            <TableRow
                              key={cfg.id}
                              className={!cfg.enabled ? "opacity-60" : ""}
                            >
                              <TableCell className="font-medium">
                                <div>{cfg.name || cfg.provider}</div>
                                {cfg.name && (
                                  <div className="text-xs text-muted-foreground">
                                    {cfg.provider}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                {cfg.unreachableSince ? (
                                  <div className="flex flex-col gap-0.5">
                                    <Badge
                                      variant="secondary"
                                      className="flex w-fit items-center gap-1 border-red-300 bg-red-100 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400"
                                    >
                                      <AlertCircle className="h-3 w-3" />
                                      {t("fleet.gps.unreachable")}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                      {t("fleet.gps.since", {
                                        date: formatDate(cfg.unreachableSince),
                                      })}
                                    </span>
                                  </div>
                                ) : cfg.enabled ? (
                                  <Badge
                                    variant="secondary"
                                    className="flex w-fit items-center gap-1 border-green-300 bg-green-100 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
                                  >
                                    <CheckCircle2 className="h-3 w-3" />
                                    {t("fleet.gps.active")}
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">
                                    {t("fleet.gps.disabled")}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell
                                className="text-sm text-muted-foreground"
                                title={
                                  cfg.plates.length > 0
                                    ? [...cfg.plates]
                                        .sort((a, b) => a.localeCompare(b))
                                        .join(", ")
                                    : undefined
                                }
                              >
                                {cfg.plates.length === 0
                                  ? "—"
                                  : (() => {
                                      const sorted = [...cfg.plates].sort(
                                        (a, b) => a.localeCompare(b),
                                      )
                                      const shown = sorted.slice(0, 3)
                                      const rest = sorted.length - shown.length
                                      return (
                                        <span>
                                          {shown.join(", ")}
                                          {rest > 0 && (
                                            <span className="ml-1 text-xs text-muted-foreground/60">
                                              {t("fleet.gps.more", { rest })}
                                            </span>
                                          )}
                                        </span>
                                      )
                                    })()}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(cfg.createdAt)}
                              </TableCell>
                              <TableCell>
                                {hasPermission("gps.manage_providers") && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingConfig(cfg)}
                                    title={t("fleet.gps.editProvider")}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

export default GpsManagement
